'use client';
import React, { useState, useCallback } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import MarketingNav from '@/app/marketing/components/MarketingNav';
import MarketingFooter from '@/app/marketing/components/MarketingFooter';

interface FormState {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  role: string;
  company: string;
  phone: string;
  city: string;
  size: string;
}

interface PasswordValidation {
  minLength: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasNumber: boolean;
  hasSpecial: boolean;
}

function getPasswordStrength(
  pw: string,
  v: PasswordValidation
): { label: string; score: number; color: string } {
  const passed = Object.values(v).filter(Boolean).length;
  if (pw.length === 0) return { label: '', score: 0, color: '' };
  if (passed <= 2) return { label: 'Weak', score: 1, color: '#ef4444' };
  if (passed === 3) return { label: 'Fair', score: 2, color: '#f59e0b' };
  if (passed === 4) return { label: 'Strong', score: 3, color: '#3b82f6' };
  return { label: 'Very Strong', score: 4, color: '#10b981' };
}

function validatePassword(pw: string): PasswordValidation {
  return {
    minLength: pw.length >= 8,
    hasUppercase: /[A-Z]/.test(pw),
    hasLowercase: /[a-z]/.test(pw),
    hasNumber: /[0-9]/.test(pw),
    hasSpecial: /[!@#$%^&*]/.test(pw),
  };
}

function EyeIcon({ open }: { open: boolean }) {
  if (open) {
    return (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
        />
      </svg>
    );
  }
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
      />
    </svg>
  );
}

function ValidationRow({ passed, label }: { passed: boolean; label: string }) {
  return (
    <div className="flex items-center gap-2">
      <div
        className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${passed ? 'bg-emerald-500/20 border border-emerald-500/40' : 'bg-white/5 border border-white/10'}`}
      >
        {passed ? (
          <svg
            className="w-2.5 h-2.5 text-emerald-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          </svg>
        ) : (
          <svg
            className="w-2.5 h-2.5 text-[#8b93a3]"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={3}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        )}
      </div>
      <span
        className={`text-xs transition-colors ${passed ? 'text-emerald-400' : 'text-[#9ca3af]'}`}
      >
        {label}
      </span>
    </div>
  );
}

export default function DemoPageClient() {
  const router = useRouter();
  const [step, setStep] = useState<'gate' | 'form'>('gate');
  const [form, setForm] = useState<FormState>({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: '',
    company: '',
    phone: '',
    city: '',
    size: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);

  const passwordValidation = validatePassword(form.password);
  const allRulesPassed = Object.values(passwordValidation).every(Boolean);
  const passwordsMatch = form.password === form.confirmPassword && form.confirmPassword.length > 0;
  const confirmMismatch = form.confirmPassword.length > 0 && form.password !== form.confirmPassword;
  const strength = getPasswordStrength(form.password, passwordValidation);

  const canSubmit =
    form.name.trim().length >= 2 &&
    form.email.trim() &&
    form.role &&
    form.company.trim().length >= 2 &&
    allRulesPassed &&
    passwordsMatch &&
    !loading;

  const handleChange = useCallback((field: keyof FormState, value: string) => {
    setForm((f) => ({ ...f, [field]: value }));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    setError('');

    // Normalize role to lowercase before sending
    const normalizedRole = form.role.trim().toLowerCase();

    try {
      const res = await fetch('/api/demo-signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          phone: form.phone || '',
          business: form.company.trim(),
          company: form.company.trim(),
          city: form.city || '',
          role: normalizedRole, // always lowercase: owner | manager | appraiser
          size: form.size || '',
          password: form.password,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Something went wrong. Please try again.');

      // Redirect to sign-in with success message
      router.push('/auth?signup=success');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const inputClass = (hasError?: boolean) =>
    `w-full px-3 py-2.5 rounded-lg bg-[#0d1120] border text-sm text-white placeholder-[#8b93a3] focus:outline-none transition-colors ${
      hasError
        ? 'border-red-500/60 focus:border-red-500'
        : 'border-white/10 focus:border-amber-500/50'
    }`;

  return (
    <div
      suppressHydrationWarning
      className="min-h-screen bg-[#0a0e1a] text-[#e5e7eb]"
      style={{ fontFamily: 'var(--font-plus-jakarta-sans), system-ui, sans-serif' }}
    >
      <MarketingNav />
      <main id="main-content">
        <section className="pt-28 pb-20 px-4 sm:px-6 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] rounded-full bg-gradient-to-b from-amber-500/6 via-violet-600/6 to-transparent blur-[120px]" />
          </div>

          <div className="max-w-5xl mx-auto relative z-10">
            {/* ── Gate Step ── */}
            {step === 'gate' && (
              <div suppressHydrationWarning className="grid lg:grid-cols-2 gap-12 items-start">
                <div suppressHydrationWarning>
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-amber-500/30 bg-amber-500/8 text-amber-300 text-xs font-medium mb-6">
                    Live Product Demo
                  </div>
                  <h1 className="text-4xl sm:text-5xl font-bold text-white mb-5 tracking-tight leading-tight">
                    See Glanz CRM
                    <br />
                    <span
                      className="bg-clip-text text-transparent"
                      style={{
                        backgroundImage: 'linear-gradient(135deg, #fbbf24, #f59e0b, #fde68a)',
                      }}
                    >
                      in Action
                    </span>
                  </h1>
                  <p className="text-[#9ca3af] text-lg mb-8 leading-relaxed">
                    Sign up for a 14-day free demo. No credit card required. Full access to all
                    features.
                  </p>

                  <div className="space-y-4 mb-8">
                    {[
                      {
                        icon: '💎',
                        title: 'Live inventory walkthrough',
                        desc: 'See how your stones would look in the system',
                      },
                      {
                        icon: '📈',
                        title: 'Rapaport pricing demo',
                        desc: 'Watch live price calculations with your markup rules',
                      },
                      {
                        icon: '🤝',
                        title: 'CRM & pipeline tour',
                        desc: 'Understand how deals flow from lead to close',
                      },
                      {
                        icon: '🔐',
                        title: 'Role & access setup',
                        desc: 'Configure your team structure in real time',
                      },
                    ].map((item) => (
                      <div
                        key={item.title}
                        className="flex items-start gap-4 p-4 rounded-xl border border-white/5 bg-[#111827]"
                      >
                        <span className="text-xl flex-shrink-0">{item.icon}</span>
                        <div>
                          <p className="text-sm font-semibold text-white">{item.title}</p>
                          <p className="text-xs text-[#9ca3af] mt-0.5">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="rounded-xl border border-white/5 bg-[#0d1120] p-5">
                    <p className="text-xs text-[#9ca3af] mb-3 uppercase tracking-wide font-semibold">
                      What merchants say
                    </p>
                    <p className="text-sm text-[#d1d5db] italic">
                      "The demo was tailored to our wholesale operation. Within 20 minutes I knew
                      this was the right tool."
                    </p>
                    <p className="text-xs text-[#9ca3af] mt-2">— David L., Bangkok Gem Wholesale</p>
                  </div>
                </div>

                <div
                  suppressHydrationWarning
                  className="rounded-2xl border border-amber-500/20 bg-gradient-to-b from-amber-500/5 to-[#111827] p-8"
                >
                  <h2 className="text-xl font-bold text-white mb-2">Start Your Free Demo</h2>
                  <p className="text-sm text-[#9ca3af] mb-6">
                    14-day full access. No credit card required.
                  </p>

                  <div className="space-y-3 mb-6">
                    {[
                      '14-day full access — all features unlocked',
                      'Confirmation email sent immediately',
                      'No credit card required',
                      'Dedicated onboarding support',
                    ].map((item) => (
                      <div key={item} className="flex items-center gap-3 text-sm text-[#d1d5db]">
                        <div className="w-5 h-5 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center flex-shrink-0">
                          <svg
                            className="w-3 h-3 text-emerald-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2.5}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        </div>
                        {item}
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={() => setStep('form')}
                    className="w-full py-4 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-[#0a0e1a] font-bold text-sm shadow-xl shadow-amber-500/20 transition-all hover:-translate-y-0.5 mb-3"
                  >
                    Sign Up for Free Demo →
                  </button>
                  <p className="text-xs text-center text-[#8b93a3]">
                    Already have an account?{' '}
                    <Link
                      href="/auth"
                      className="text-amber-400 hover:text-amber-300 transition-colors"
                    >
                      Log in
                    </Link>
                  </p>
                </div>
              </div>
            )}

            {/* ── Form Step ── */}
            {step === 'form' && (
              <div suppressHydrationWarning className="max-w-xl mx-auto">
                <button
                  onClick={() => setStep('gate')}
                  className="flex items-center gap-1.5 text-sm text-[#9ca3af] hover:text-white transition-colors mb-6"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                  Back
                </button>

                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-white mb-2">Create Your Demo Account</h2>
                  <p className="text-sm text-[#9ca3af]">
                    14-day free trial · No credit card required
                  </p>
                </div>

                <div className="rounded-2xl border border-amber-500/20 bg-[#111827]/80 backdrop-blur-sm p-7 shadow-2xl">
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Full Name */}
                    <div>
                      <label className="block text-xs font-medium text-[#9ca3af] mb-1.5">
                        Full Name <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        value={form.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        placeholder="John Smith"
                        className={inputClass(!form.name.trim() && form.name !== '')}
                        minLength={2}
                        required
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-xs font-medium text-[#9ca3af] mb-1.5">
                        Email <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="email"
                        value={form.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        placeholder="you@company.com"
                        className={inputClass()}
                        required
                      />
                    </div>

                    {/* Role */}
                    <div>
                      <label className="block text-xs font-medium text-[#9ca3af] mb-1.5">
                        Role <span className="text-red-400">*</span>
                      </label>
                      <select
                        value={form.role}
                        onChange={(e) => handleChange('role', e.target.value)}
                        className="w-full px-3 py-2.5 rounded-lg bg-[#0d1120] border border-white/10 text-sm text-white focus:outline-none focus:border-amber-500/50 transition-colors appearance-none"
                        required
                      >
                        <option value="">Select your role...</option>
                        <option value="owner">Owner</option>
                        <option value="manager">Manager</option>
                        <option value="appraiser">Appraiser</option>
                      </select>
                    </div>

                    {/* Company Name */}
                    <div>
                      <label className="block text-xs font-medium text-[#9ca3af] mb-1.5">
                        Company Name <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        value={form.company}
                        onChange={(e) => handleChange('company', e.target.value)}
                        placeholder="Siam Diamond Co."
                        className={inputClass()}
                        minLength={2}
                        required
                      />
                    </div>

                    {/* Password */}
                    <div>
                      <label className="block text-xs font-medium text-[#9ca3af] mb-1.5">
                        Password <span className="text-red-400">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={form.password}
                          onChange={(e) => {
                            handleChange('password', e.target.value);
                            setPasswordTouched(true);
                          }}
                          placeholder="Min 8 chars, 1 uppercase, 1 number, 1 special"
                          className={`${inputClass()} pr-10`}
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((v) => !v)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8b93a3] hover:text-[#9ca3af] transition-colors"
                        >
                          <EyeIcon open={showPassword} />
                        </button>
                      </div>

                      {/* Password strength */}
                      {form.password.length > 0 && (
                        <div className="mt-2">
                          <div className="flex gap-1 mb-1.5">
                            {[1, 2, 3, 4].map((i) => (
                              <div
                                key={i}
                                className="h-1 flex-1 rounded-full transition-all"
                                style={{
                                  backgroundColor: i <= strength.score ? strength.color : '#1f2937',
                                }}
                              />
                            ))}
                          </div>
                          {strength.label && (
                            <p className="text-xs mb-2" style={{ color: strength.color }}>
                              {strength.label}
                            </p>
                          )}
                        </div>
                      )}

                      {/* Validation rules */}
                      {passwordTouched && (
                        <div className="mt-2 grid grid-cols-2 gap-1.5">
                          <ValidationRow
                            passed={passwordValidation.minLength}
                            label="8+ characters"
                          />
                          <ValidationRow
                            passed={passwordValidation.hasUppercase}
                            label="Uppercase letter"
                          />
                          <ValidationRow passed={passwordValidation.hasNumber} label="Number" />
                          <ValidationRow
                            passed={passwordValidation.hasSpecial}
                            label="Special character"
                          />
                        </div>
                      )}
                    </div>

                    {/* Confirm Password */}
                    <div>
                      <label className="block text-xs font-medium text-[#9ca3af] mb-1.5">
                        Confirm Password <span className="text-red-400">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          value={form.confirmPassword}
                          onChange={(e) => handleChange('confirmPassword', e.target.value)}
                          placeholder="Repeat your password"
                          className={`${inputClass(confirmMismatch)} pr-10`}
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword((v) => !v)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8b93a3] hover:text-[#9ca3af] transition-colors"
                        >
                          <EyeIcon open={showConfirmPassword} />
                        </button>
                      </div>
                      {confirmMismatch && (
                        <p className="text-xs text-red-400 mt-1">Passwords do not match</p>
                      )}
                      {passwordsMatch && (
                        <p className="text-xs text-emerald-400 mt-1 flex items-center gap-1">
                          <svg
                            className="w-3 h-3"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2.5}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                          Passwords match
                        </p>
                      )}
                    </div>

                    {/* Optional fields */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-[#9ca3af] mb-1.5">
                          Phone <span className="text-[#8b93a3]">(optional)</span>
                        </label>
                        <input
                          type="text"
                          value={form.phone}
                          onChange={(e) => handleChange('phone', e.target.value)}
                          placeholder="+1 555 000 0000"
                          className={inputClass()}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-[#9ca3af] mb-1.5">
                          City <span className="text-[#8b93a3]">(optional)</span>
                        </label>
                        <input
                          type="text"
                          value={form.city}
                          onChange={(e) => handleChange('city', e.target.value)}
                          placeholder="Bangkok"
                          className={inputClass()}
                        />
                      </div>
                    </div>

                    {error && (
                      <div className="flex items-start gap-2 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                        <svg
                          className="w-4 h-4 flex-shrink-0 mt-0.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <span>{error}</span>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={!canSubmit}
                      className="w-full py-4 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-[#0a0e1a] font-bold text-sm shadow-xl shadow-amber-500/20 transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                    >
                      {loading ? (
                        <span className="flex items-center justify-center gap-2">
                          <div className="w-4 h-4 border-2 border-[#0a0e1a] border-t-transparent rounded-full animate-spin" />
                          Creating account...
                        </span>
                      ) : (
                        'Create Demo Account →'
                      )}
                    </button>

                    <p className="text-xs text-center text-[#8b93a3]">
                      By signing up you agree to our{' '}
                      <Link
                        href="/privacy"
                        className="text-amber-400/70 hover:text-amber-400 transition-colors"
                      >
                        Privacy Policy
                      </Link>
                    </p>
                  </form>
                </div>

                <p className="text-center text-xs text-[#8b93a3] mt-4">
                  Already have an account?{' '}
                  <Link
                    href="/auth"
                    className="text-amber-400 hover:text-amber-300 transition-colors"
                  >
                    Sign in
                  </Link>
                </p>
              </div>
            )}
          </div>
        </section>
      </main>
      <MarketingFooter />
    </div>
  );
}
